import { Directive, ElementRef, computed, inject, input } from '@angular/core';
import { insertTextAtCursor } from './insert-text-at-cursor.util';

const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

/**
 * Directive that prevents specific characters from being typed or pasted
 * into an input element.
 *
 * Intercepts `keydown` events to block forbidden keystrokes, `beforeinput`
 * events to catch OS-level text substitutions (e.g. the macOS
 * "double-space → period" feature), and `paste` events to strip forbidden
 * characters from the clipboard text before inserting it at the current
 * cursor position.
 *
 * Keyboard shortcuts that use Ctrl, Meta, or Alt as a modifier (e.g.
 * Ctrl+V, Cmd+A) are always allowed.
 *
 * @example
 * ```html
 * <input idtPreventChars [preventChars]="['@', '#', '$']" />
 * ```
 */
@Directive({
  selector: '[idtPreventChars]',
  standalone: true,
  host: {
    '(beforeinput)': 'onBeforeInput($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
  },
})
export class IdtPreventCharsDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  /**
   * List of characters to block.
   * Regex special characters are automatically escaped when building the
   * internal filter pattern.
   */
  readonly preventChars = input<string[]>([]);

  /**
   * Memoized regex built from the current `preventChars` list. Rebuilt only
   * when the input signal changes. Returns `null` when the list is empty so
   * the strip function can short-circuit without allocating a new RegExp.
   */
  private readonly stripRegex = computed(() => {
    const chars = this.preventChars();
    if (chars.length === 0) {
      return null;
    }
    const escaped = chars
      .map((c) => c.replace(REGEX_SPECIAL, String.raw`\$&`))
      .join('');
    return new RegExp(`[${escaped}]`, 'g');
  });

  /**
   * Intercepts `beforeinput` events to block OS-level text substitutions
   * (e.g. the macOS "double-space → period" feature) that would insert
   * forbidden characters.
   *
   * @param event - The native `InputEvent`.
   */
  onBeforeInput(event: InputEvent): void {
    const { data } = event;
    if (data !== null && this.stripForbiddenChars(data) !== data) {
      event.preventDefault();
    }
  }

  /**
   * Blocks the keystroke when the pressed key matches one of the forbidden
   * characters. Keyboard shortcuts that use Ctrl, Meta, or Alt as a modifier
   * are always allowed.
   *
   * @param event - The native keyboard event.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (this.preventChars().includes(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Intercepts paste events, strips forbidden characters from the
   * clipboard text, and inserts the cleaned result at the current
   * cursor / selection position.
   *
   * Dispatches a synthetic `input` event so that Angular forms and
   * other listeners are notified of the value change.
   *
   * @param event - The native clipboard event.
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') ?? '';
    const cleaned = this.stripForbiddenChars(pastedText);

    if (!cleaned) {
      return;
    }

    insertTextAtCursor(this.el.nativeElement, cleaned);
  }

  /**
   * Removes all forbidden characters from the given string using the
   * memoized regex. Returns the original string unchanged when no
   * characters are forbidden.
   *
   * @param value - The raw string to sanitize.
   * @returns The sanitized string with forbidden characters removed.
   */
  private stripForbiddenChars(value: string): string {
    const re = this.stripRegex();
    return re ? value.replace(re, '') : value;
  }
}
